import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { useUser } from "@clerk/react";
import { PageHeader } from "../PageHeader";
import { MetricCard } from "../shared/MetricCard";
import { FilterChips } from "../shared/FilterChips";
import { SectionBoundary } from "../shared/SectionBoundary";
import { DailyStackChart, type DayValues } from "../shared/DailyStackChart";
import { usePeople } from "../aiUsage/usePeople";
import { nextLocalMidnight, useNow } from "../aiUsage/useNow";
import {
  TOOL_STATUS_LABEL,
  formatCount,
  formatSpend,
  formatWhen,
  notRoutedWhy,
  periodStart,
  reasonLabel,
  spendParts,
} from "../aiUsage/format";
import { api as convexApi } from "../../../lib/convexApi";

type Period = "7" | "30";
type ToolStatus = "sanctioned" | "review" | "not_allowed";

// The result of convex/observability/queries.ts `overview`.
interface Overview {
  scope: "org" | "self";
  truncated: boolean;
  coverage: {
    gateway: {
      sessions: number;
      routed: number;
      notRouted: number;
      reasons: Record<string, number>;
    };
  };
  // Sorted by sessions, then name.
  tools: Array<{
    tool: string;
    toolName: string;
    status: ToolStatus;
    people: number;
    sessions: number;
    costMicroUsd: number;
    // Of the calls made as the tool, those Convex could not price.
    unpricedCalls: number;
    lastSeenAt: number | null;
  }>;
  kpis: {
    sessions: number;
    people: number;
    calls: number;
    unpricedCalls: number;
    costMicroUsd: number;
    flags: number;
  };
  activity: Array<{ day: string; byTool: Record<string, number> }>;
  // The first 10 flags; kpis.flags counts them all.
  flags: Array<
    | {
        kind: "dlp";
        sessionId: string;
        userId: string;
        toolName: string;
        at: number;
        count: number;
      }
    | { kind: "not-routed"; reason: string; sessions: number }
    | {
        kind: "tool-status";
        tool: string;
        toolName: string;
        status: "review" | "not_allowed";
        sessions: number;
      }
  >;
}

// Convex's key for the rest of a capped list (convex/observability/lib.ts
// OTHER): in activity, the calls of every tool the overview does not list; in
// the reasons, those past the top 10. It is not a tool, so it never goes to a
// tool's name lookup.
const FOLDED = "~other";
const OTHER_TOOLS_LABEL = "Other tools";

const DAY_MS = 86_400_000;

// Sources later sub-projects add. They are listed as what Steerly does NOT see
// yet, so the page never implies more coverage than it has (spec section 10).
const LATER_SOURCES = [
  {
    name: "Coding agents outside Steerly",
    adds: "Claude Code and Codex sessions started in any terminal.",
  },
  {
    name: "Browser AI",
    adds: "ChatGPT, Claude and Gemini used in the browser, through a browser extension.",
  },
  {
    name: "Desktop and IDE AI apps",
    adds: "AI desktop apps and IDE assistants, seen by the Steerly host.",
  },
];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function compareKeys(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function statusPillClass(status: ToolStatus): string {
  if (status === "sanctioned") return "sr-pill is-ok";
  if (status === "not_allowed") return "sr-pill is-crit";
  return "sr-pill is-warn";
}

// Every day of the period from `since`, oldest first, so a day without calls
// is drawn as an empty slot rather than left out. Activity on a later day (the
// page was left open past UTC midnight) is kept too.
function periodDays(since: number, days: number, activity: Overview["activity"]): DayValues[] {
  const byDay = new Map(activity.map((a) => [a.day, a.byTool]));
  const keys = new Set<string>();
  for (let i = 0; i < days; i += 1) {
    keys.add(new Date(since + i * DAY_MS).toISOString().slice(0, 10));
  }
  for (const a of activity) keys.add(a.day);
  return [...keys].sort().map((day) => ({ day, values: byDay.get(day) ?? {} }));
}

// Calls per activity key over the period. A Map, since the keys come from the
// server and a tool key such as "constructor" must not meet Object.prototype.
function callTotals(activity: Overview["activity"]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const a of activity) {
    for (const [key, n] of Object.entries(a.byTool)) totals.set(key, (totals.get(key) ?? 0) + n);
  }
  return totals;
}

// The chart's series: every key in the activity, busiest first (ties by key),
// with the calls of unlisted tools last; the chart draws those as its fold
// (foldKey). Built from the activity rather than from the tools' sessions, so a
// tool used only through model calls is drawn.
function activitySeries(totals: Map<string, number>): string[] {
  const keys = [...totals.keys()].filter((k) => k !== FOLDED);
  keys.sort((a, b) => totals.get(b)! - totals.get(a)! || compareKeys(a, b));
  return totals.has(FOLDED) ? [...keys, FOLDED] : keys;
}

export function AiOverviewPage({ onOpenSession }: { onOpenSession: (id: string) => void }) {
  const [period, setPeriod] = useState<Period>("7");
  const days = Number(period);
  // The clock the page's period and times are read from, read again at each
  // UTC midnight, so a page left open overnight moves its period on a day
  // instead of showing one day more, and at each local midnight, when the
  // times shown as today's become yesterday's (useNow also reads it when the
  // page comes back after a sleep).
  const now = useNow((n) => Math.min((Math.floor(n / DAY_MS) + 1) * DAY_MS, nextLocalMidnight(n)));
  const since = useMemo(() => periodStart(days, now), [days, now]);
  return (
    <>
      <PageHeader
        title="AI overview"
        subtitle={`last ${period} days`}
        actions={
          <FilterChips
            label="Period"
            value={period}
            onChange={setPeriod}
            options={[
              { value: "7", label: "7 days" },
              { value: "30", label: "30 days" },
            ]}
          />
        }
      />
      <div className="sr-page-body">
        {/* Keyed by the period's start: a new period is a new query, so a
            failure on the last one does not stand in for it. */}
        <SectionBoundary key={since} label="AI overview">
          <OverviewBody since={since} days={days} now={now} onOpenSession={onOpenSession} />
        </SectionBoundary>
      </div>
    </>
  );
}

function OverviewBody({
  since,
  days,
  now,
  onOpenSession,
}: {
  since: number;
  days: number;
  now: number;
  onOpenSession: (id: string) => void;
}) {
  const data = useQuery((convexApi as any).observability.queries.overview, { since }) as
    | Overview
    | undefined;
  const { nameFor, capped } = usePeople();
  const { user } = useUser();
  const me = user?.id ?? null;
  const toolNames = useMemo(
    () => new Map((data?.tools ?? []).map((t) => [t.tool, t.toolName])),
    [data]
  );
  const chart = useMemo(() => {
    const activity = data?.activity ?? [];
    const calls = callTotals(activity);
    return { days: periodDays(since, days, activity), series: activitySeries(calls), calls };
  }, [data, since, days]);
  if (data === undefined) return <div className="sr-empty">Loading AI usage…</div>;

  const gw = data.coverage.gateway;
  // Commonest first, the folded rest last. Convex does not keep an object's
  // field order, so the order is set here.
  const reasons = Object.entries(gw.reasons).sort(
    ([ka, a], [kb, b]) =>
      Number(ka === FOLDED) - Number(kb === FOLDED) || b - a || compareKeys(ka, kb)
  );
  // The chart draws FOLDED itself (foldKey), so only tool keys reach here.
  const toolLabel = (key: string) => toolNames.get(key) ?? key;
  const moreFlags = data.kpis.flags - data.flags.length;
  const spend = spendParts(data.kpis.costMicroUsd, data.kpis.calls, data.kpis.unpricedCalls);
  // A note on prices only under a price: not under "not priced" or "no calls seen".
  let spendNote: string | undefined;
  if (spend.priced) {
    spendNote = spend.unpriced ? `at API list prices; ${spend.unpriced}` : "at API list prices";
  }

  return (
    <>
      {data.scope === "self" && <div className="sr-ai-scope">Showing your sessions</div>}
      {data.truncated && (
        <div className="sr-ai-note">
          These figures cover the busiest tools and the newest sessions only. This period has more
          than the overview counts, and one person's heavy use can push older sessions out of the
          period's view.
        </div>
      )}

      <h2 className="sr-section-title">What Steerly can see</h2>
      <div className="sr-ai-sources">
        <div className="sr-ai-source is-live">
          <div className="sr-ai-source__head">
            <strong>Steerly sessions</strong>
            <span className="sr-pill is-ok">live</span>
          </div>
          <div className="sr-ai-source__body">
            {gw.sessions === 0
              ? "No Steerly terminals or chats in this period."
              : `${gw.routed} of ${plural(gw.sessions, "session", "sessions")} routed through the AI gateway`}
          </div>
          {gw.notRouted > 0 && (
            <div className="sr-ai-source__note">
              Not routed: {reasons.map(([reason, n]) => `${reasonLabel(reason)} (${n})`).join(", ")}
            </div>
          )}
        </div>
        {LATER_SOURCES.map((s) => (
          <div key={s.name} className="sr-ai-source">
            <div className="sr-ai-source__head">
              <strong>{s.name}</strong>
              <span className="sr-pill">not connected yet</span>
            </div>
            <div className="sr-ai-source__body">Will add: {s.adds}</div>
          </div>
        ))}
      </div>

      {data.kpis.sessions === 0 && data.kpis.calls === 0 ? (
        // What the host records (src/observability/index.js): a session for a
        // supported AI agent started in a Steerly terminal or chat by a signed-in
        // user, routed or not, and model calls only for a routed session.
        <div className="sr-empty">
          {data.scope === "self"
            ? "No AI sessions of yours in this period. Steerly records a session when you start Claude Code, Codex or another supported AI agent in a Steerly terminal or chat, and its model calls when the session goes through the AI gateway."
            : "No AI sessions in this period. Steerly records a session when someone starts Claude Code, Codex or another supported AI agent in a Steerly terminal or chat, and its model calls when the session goes through the AI gateway."}
        </div>
      ) : (
        <>
          <h2 className="sr-section-title">AI tools in use</h2>
          <table className="sr-table">
            <thead>
              <tr>
                <th>Tool</th>
                <th className="is-num">People</th>
                <th className="is-num">Sessions</th>
                <th className="is-num">Spend</th>
                <th>Status</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.tools.map((t) => (
                <tr key={t.tool}>
                  <td>{t.toolName}</td>
                  <td className="is-num">{formatCount(t.people)}</td>
                  <td className="is-num">{formatCount(t.sessions)}</td>
                  <td className="is-num">
                    {formatSpend(t.costMicroUsd, chart.calls.get(t.tool) ?? 0, t.unpricedCalls)}
                  </td>
                  <td>
                    <span className={statusPillClass(t.status)}>{TOOL_STATUS_LABEL[t.status]}</span>
                  </td>
                  <td>{t.lastSeenAt ? formatWhen(t.lastSeenAt, now) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 className="sr-section-title">Activity</h2>
          <div className="sr-metric-grid">
            <MetricCard label="Sessions" value={formatCount(data.kpis.sessions)} />
            <MetricCard label="People" value={formatCount(data.kpis.people)} />
            <MetricCard label="Spend" value={spend.amount} delta={spendNote} />
            <MetricCard
              label="Flags"
              value={formatCount(data.kpis.flags)}
              tone={data.kpis.flags > 0 ? "ask" : "neutral"}
            />
          </div>
          <div className="sr-card sr-card--chart" style={{ marginTop: 12 }}>
            <DailyStackChart
              title="Model calls per day, by tool"
              days={chart.days}
              series={chart.series}
              labelFor={toolLabel}
              foldKey={FOLDED}
              otherLabel={OTHER_TOOLS_LABEL}
              formatValue={formatCount}
              emptyText="No model calls in this period"
            />
          </div>

          <h2 className="sr-section-title">Flags</h2>
          {data.flags.length === 0 ? (
            <div className="sr-empty">Nothing flagged in this period.</div>
          ) : (
            <ul className="sr-ai-flags">
              {data.flags.map((f, i) => {
                if (f.kind === "dlp") {
                  const findings = plural(f.count, "DLP finding", "DLP findings");
                  const where =
                    f.userId === me
                      ? `in your ${f.toolName} session`
                      : `in a ${f.toolName} session of ${nameFor(f.userId)}`;
                  return (
                    <li key={`dlp-${f.sessionId}`} className="sr-ai-flag is-deny">
                      <button
                        type="button"
                        className="sr-link"
                        onClick={() => onOpenSession(f.sessionId)}
                      >
                        {findings} {where}, last seen {formatWhen(f.at, now)}
                      </button>
                    </li>
                  );
                }
                if (f.kind === "not-routed") {
                  return (
                    <li key={`nr-${f.reason}`} className="sr-ai-flag is-ask">
                      {plural(f.sessions, "session", "sessions")}{" "}
                      {notRoutedWhy("not routed through the gateway", f.reason)}
                    </li>
                  );
                }
                // A tool is flagged when it was used in the period, through
                // sessions started as it or through model calls made as it.
                const use =
                  f.sessions > 0
                    ? `was used in ${plural(f.sessions, "session", "sessions")}`
                    : "made model calls in this period";
                return (
                  <li key={`tool-${f.tool}-${i}`} className="sr-ai-flag is-ask">
                    {f.toolName} is marked {TOOL_STATUS_LABEL[f.status]} and {use}
                  </li>
                );
              })}
              {moreFlags > 0 && <li className="sr-ai-note">and {moreFlags} more</li>}
            </ul>
          )}
          {capped && data.flags.some((f) => f.kind === "dlp") && (
            <div className="sr-ai-note">
              Names of people past the org's first 1,000 members show as short ids.
            </div>
          )}
        </>
      )}
    </>
  );
}
